'use strict';

import React, {createContext, useContext, useCallback, useState, useEffect} from 'react';
import {encodeArray} from '../../shared/pocker-son';
import {Map as ImmutableMap, OrderedMap} from 'immutable';
import {apiCall} from '../../shared/api-client';

class EventsTreeCl {
	constructor() {
		this._nodes = new Map();
		this._roots = new Map();
		this._ready = false;
		this._loading = null;
	}

	addNode(type) {
		let nodeID = encodeArray(type);
		let node = this._nodes.get(nodeID);
		if (!node) {
			node = {id: nodeID, type, children: new Map()};
			this._nodes.set(nodeID, node);
		}

		let parent = type.slice(0, -1);
		if (!parent.length) {
			this._roots.set(nodeID, type);
		} else {
			// let parentID = encodeArray(parent);
			parent = this.addNode(parent);
			parent.children.set(nodeID, node);
		}
		return node;
	}

	getNode(type) {
		let nodeID = encodeArray(type);
		return this._nodes.get(nodeID);
	}

	load() {
		if (this._ready) {
			return Promise.resolve(this);
		}

		if (!this._loading) {
			this._loading = Promise.resolve()
				.then(async () => {
					let response = await apiCall({method: 'stats/events-tree'});
					for (let type of response.data) {
						this.addNode(type);
					}
					this._ready = true;
					this._loading = null;
					return this;
				});
		}

		return Promise.resolve(this._loading);
	}

	get ready() {
		return this._ready;
	}

	get nodes() {
		return this._nodes;
	}

	get roots() {
		return this._roots;
	}

	[Symbol.iterator]() {
		return this._nodes.values();
	}
}

let _tree = new EventsTreeCl();

const ToggleNode = Symbol('ToggleNode');
const ShowNode = Symbol('ShowNode');
const HideNode = Symbol('HideNode');
const SetFilter = Symbol('SetFilter');

const MaxSelectedNodes = 6;
const StartingNodes = [['Validate'], ['Token']];
const StartingFilter = {};
const InitialState = new ImmutableMap([
	['nodes', new OrderedMap(
		StartingNodes.map(t => {
			let node = _tree.addNode(t);
			return [node.id, node];
		}))
	],
	['filter', new ImmutableMap(StartingFilter)]
]);

class StatsStateReducer {
	static _showTreeNode(state, node) {
		return state.withMutations(s => {
			s.setIn(['nodes', node.id], node);
			let nodes = s.get('nodes');
			if (nodes.size > MaxSelectedNodes) {
				let k = nodes.keySeq().first();
				console.debug(`Selected more nodes than i can handle atm, deselecting the oldest one`, k);
				s.deleteIn(['nodes', k]);
			}
		});
	}

	static _hideTreeNode(state, node) {
		let nodes = state.get('nodes');
		if (nodes.size <= 1) {
			console.debug(`Deselected the last node so i revert it to initial selection for now`);
			return state.set('nodes', InitialState.get('nodes'));
		} else {
			return state.deleteIn(['nodes', node.id]);
		}
	}

	static [ToggleNode](state, node) {
		if (state.hasIn(['nodes', node.id])) {
			return this._hideTreeNode(state, node);
		} else {
			return this._showTreeNode(state, node);
		}
	}

	static [ShowNode](state, node) {
		if (!state.hasIn(['nodes', node.id])) {
			return this._showTreeNode(state, node);
		}
	}

	static [HideNode](state, node) {
		if (state.hasIn(['nodes', node.id])) {
			return this._hideTreeNode(state, node);
		}
	}

	static [SetFilter](state, field, value) {
		if (Array.isArray(value) && value[0] === '*') {
			return state.deleteIn(['filter', field]);
		} else {
			return state.setIn(['filter', field], value);
		}
	}
}

const StatsStateContext = createContext(null);

function StatsStateProvider({children}) {
	const [state, setState] = useState(InitialState);
	const dispatch = useCallback((type, ...args) => {
		setState((state) => {
			try {
				state = StatsStateReducer[type](state, ...args);
			} catch (err) {
				console.error('Error calling state action', type);
				console.error(err);
			}
			return state;
		});
	}, [setState]);

	return (
		<StatsStateContext.Provider value={[state, dispatch]}>
			{children}
		</StatsStateContext.Provider>
	);
}

function useStatsState() {
	return useContext(StatsStateContext);
}

function useNodes() {
	const [state, dispatch] = useContext(StatsStateContext);
	return [state.get('nodes'), dispatch];
}

function useFilter() {
	const [state, dispatch] = useContext(StatsStateContext);
	return [state.get('filter'), dispatch];
}

function useTree() {
	const [tree, setTree] = useState(_tree.ready ? _tree : null);
	useEffect(() => {
		if (tree !== null) {
			return;
		}
		let canceled = false;
		(async () => {
			await _tree.load();
			if (!canceled) {
				setTree(_tree);
			}
		})();
		return () => {
			canceled = true;
		}
	}, []);
	return tree;
}

export {
	StatsStateProvider,
	ToggleNode, ShowNode, HideNode, SetFilter,
	useStatsState, useNodes, useFilter,
	useTree
};